import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import gameApi from '../services/GameApiService';
import type { Highscore } from '../services/GameApiService';

interface Adventure {
  id: string;
  title: string;
}

export default function HighscoresPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [highscores, setHighscores] = useState<Highscore[]>([]);
  const [adventures, setAdventures] = useState<Adventure[]>([]);
  const [selectedAdventure, setSelectedAdventure] = useState<string>('');
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  useEffect(() => {
    loadHighscores();
  }, [selectedAdventure]);

  const loadHighscores = async () => {
    try {
      setIsLoading(true);
      const data = await gameApi.getHighscores(selectedAdventure || undefined, 50);
      setHighscores(data.highscores);
      setAdventures(data.adventures);
      setIsLoading(false);
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao carregar ranking';
      
      // If it's a "Failed to fetch" error and we haven't exceeded retries, try again
      if (errorMessage.includes('Failed to fetch') && retryCount < maxRetries) {
        console.log(`[HighscoresPage] Network error, retry ${retryCount + 1}/${maxRetries}`);
        setRetryCount(retryCount + 1);
        // Retry after a delay (exponential backoff: 1s, 2s, 4s)
        setTimeout(() => {
          loadHighscores();
        }, Math.pow(2, retryCount) * 1000);
      } else {
        // Either not a network error, or we've exceeded retries
        setError(errorMessage);
        setIsLoading(false);
      }
    }
  };

  // Get medal for top 3
  const getMedal = (rank: number) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return null;
    }
  };

  // Get score tier color
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-orange-600';
  };

  if (isLoading && highscores.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="A carregar ranking..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-dark mb-4">
            Oops! Algo correu mal
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link 
            to="/" 
            className="inline-block px-6 py-3 bg-primary text-white rounded-xl hover:bg-opacity-90 transition-colors"
          >
            Voltar ao início
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-5xl mb-4">🏆</div>
          <h1 className="text-3xl font-bold text-dark">
            Ranking de Jogadores
          </h1>
          <p className="text-gray-600 mt-2">
            Os melhores aventureiros
          </p>
        </div>

        {/* Filter */}
        {adventures.length > 1 && (
          <div className="mb-6">
            <select
              value={selectedAdventure}
              onChange={(e) => setSelectedAdventure(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 bg-white"
            >
              <option value="">Todas as Aventuras</option>
              {adventures.map((adv) => (
                <option key={adv.id} value={adv.id}>
                  {adv.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Loading overlay for filter change */}
        {isLoading && highscores.length > 0 && (
          <div className="text-center mb-4">
            <LoadingSpinner size="sm" />
          </div>
        )}

        {/* Highscores Table */}
        {highscores.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <div className="text-4xl mb-4">🎮</div>
            <h3 className="text-lg font-semibold text-dark mb-2">
              Ainda não há pontuações
            </h3>
            <p className="text-gray-600 mb-6">
              Sê o primeiro a completar uma aventura e entrar no ranking!
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-gradient-to-r from-primary to-accent text-white font-medium rounded-xl hover:shadow-lg transition-all"
            >
              Começar Aventura
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100 text-sm font-medium text-gray-500">
              <div className="col-span-1 text-center">#</div>
              <div className="col-span-4 sm:col-span-3">Nome</div>
              <div className="col-span-2 text-center">Pontos</div>
              <div className="col-span-5 sm:col-span-4 hidden sm:block">Aventura</div>
              <div className="col-span-2 text-center hidden sm:block">Episódios</div>
            </div>

            {/* Table Rows */}
            {highscores.map((hs, index) => {
              const rank = hs.rank || index + 1;
              const medal = getMedal(rank);
              
              return (
                <div
                  key={hs.id}
                  className={`grid grid-cols-12 gap-2 px-4 py-4 border-b border-gray-50 items-center hover:bg-gray-50 transition-colors ${
                    rank <= 3 ? 'bg-gradient-to-r from-yellow-50/50 to-transparent' : ''
                  }`}
                >
                  {/* Rank */}
                  <div className="col-span-1 text-center">
                    {medal ? (
                      <span className="text-xl">{medal}</span>
                    ) : (
                      <span className="text-gray-400 font-medium">{rank}</span>
                    )}
                  </div>

                  {/* Name */}
                  <div className="col-span-4 sm:col-span-3">
                    <span className="font-medium text-dark truncate block">
                      {hs.playerName}
                    </span>
                  </div>

                  {/* Score */}
                  <div className="col-span-2 text-center">
                    <span className={`font-bold text-lg ${getScoreColor(hs.score)}`}>
                      {hs.score}
                    </span>
                  </div>

                  {/* Adventure */}
                  <div className="col-span-5 sm:col-span-4 hidden sm:block">
                    <span className="text-sm text-gray-600 truncate block">
                      {hs.adventureTitle}
                    </span>
                  </div>

                  {/* Episodes */}
                  <div className="col-span-2 text-center hidden sm:block">
                    <span className="text-sm text-gray-500">
                      {hs.episodesCompleted}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Action Button */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-block px-8 py-4 bg-gradient-to-r from-primary to-accent text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all"
          >
            Nova Aventura
          </Link>
        </div>
      </div>
    </div>
  );
}
